import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ShopLayout from './ShopLayout.js';
import { subscriptionPlanApi } from '../../services/subscriberApi.js';
import {
  MONTH_BASELINE_PRINT,
  calcSavingsPct,
  avgPerMonth,
  parseFeatures,
  maxPlanSavingsPct,
  findPlanByMonths,
} from '../../utils/planPricing.js';

const FLARE_RED = '#c4151c';

const Icon = ({ path, style, className = '' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    style={style}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
  </svg>
);

const ICON_STYLE_14 = { width: '14px', height: '14px' };
const ICON_STYLE_16 = { width: '16px', height: '16px' };
const ICON_STYLE_18 = { width: '18px', height: '18px' };
const ICON_STYLE_32 = { width: '32px', height: '32px' };

function durationLabel(months) {
  if (months === 1) return 'Monthly';
  if (months === 3) return 'Quarterly';
  if (months === 6) return 'Half-Yearly';
  if (months === 12) return 'Yearly · BEST VALUE';
  if (months === 24) return 'Biennial · MAX SAVINGS';
  return `${months} Months`;
}

function PlanCard({ plan }) {
  const navigate = useNavigate();
  const months = Number(plan.duration_months) || 1;
  const total = Number(plan.price_pkr) || 0;
  const avg = avgPerMonth(total, months);
  const savePct = calcSavingsPct(months, total);
  const features = parseFeatures(plan.features);
  const isBest = months === 12;
  const isMax = months === 24;

  return (
    <div className="h-full">
      <div
        className={`relative bg-white rounded-3xl border shadow-md overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl flex flex-col h-full ${isBest ? 'ring-4' : 'border-gray-100'}`}
        style={isBest ? { borderColor: FLARE_RED, borderWidth: '2px', ringColor: 'rgba(196,21,28,0.1)' } : {}}
      >
        {isBest && (
          <div
            className="text-center py-2 text-xs font-black uppercase tracking-widest text-white shadow"
            style={{ backgroundColor: FLARE_RED, letterSpacing: '0.12em' }}
          >
            &#11088; Most Popular · Readers' Choice
          </div>
        )}
        {isMax && !isBest && (
          <div className="text-center py-2 text-xs font-black uppercase tracking-widest text-amber-900 border-b border-amber-200"
            style={{ backgroundImage: 'linear-gradient(to right,#fef3c7,#fffbeb,#fef3c7)' }}
          >
            &#127942; Lowest Price Per Issue · 2 Year Lock-In
          </div>
        )}

        <div className={`px-6 pt-6 pb-5 flex-1 flex flex-col ${isBest || isMax ? 'mt-2' : ''}`}>
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <h3 className="font-black text-xl text-gray-900 leading-tight mb-2">{plan.name}</h3>
              <span
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-gray-100 text-gray-700"
                style={{ letterSpacing: '0.08em' }}
              >
                {durationLabel(months)} · {months} Issue{months > 1 ? 's' : ''}
              </span>
            </div>
            {savePct > 0 && (
              <span
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-black text-white shadow-sm"
                style={{ backgroundColor: '#059669', whiteSpace: 'nowrap' }}
              >
                <Icon
                  path="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                  style={ICON_STYLE_14}
                />
                SAVE {savePct}% OFF
              </span>
            )}
          </div>

          <div className="mb-5">
            <div className="flex items-baseline gap-3 flex-wrap">
              <span
                className="text-4xl lg:text-5xl font-black tracking-tight"
                style={{ color: FLARE_RED }}
              >
                Rs {total.toLocaleString('en-PK')}
              </span>
              <span className="text-sm font-bold text-gray-400 line-through">
                Rs {(MONTH_BASELINE_PRINT * months).toLocaleString('en-PK')}
              </span>
            </div>
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-50 border border-gray-200">
              <Icon
                path="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                style={ICON_STYLE_16}
                className="text-gray-500"
              />
              <span className="text-xs font-bold text-gray-700">
                Avg Rs {avg.toLocaleString('en-PK')} <span className="text-gray-400">/ month</span>
              </span>
            </div>
          </div>

          <div
            className="flex items-center gap-3 mb-5 px-4 py-3 rounded-2xl border"
            style={{ backgroundColor: '#ecfdf5', borderColor: '#a7f3d0' }}
          >
            <Icon
              path="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
              style={ICON_STYLE_18}
              className="text-emerald-600 shrink-0"
            />
            <span className="text-sm font-bold text-emerald-800 leading-snug">
              FREE nationwide courier delivery on every issue
            </span>
          </div>

          <ul className="space-y-3 mb-6 flex-1">
            {(features.length ? features : ['Printed magazine copy', 'Doorstep delivery each month', 'Exclusive subscriber discounts']).map((ft, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
                <Icon
                  path="M5 13l4 4L19 7"
                  style={ICON_STYLE_18}
                  className="text-emerald-600 shrink-0 mt-0.5"
                />
                <span className="font-medium leading-snug">{ft}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="px-6 pb-6 mt-auto pt-1 bg-gradient-to-b from-transparent to-gray-50/60">
          <button
            type="button"
            onClick={() => navigate(`/checkout?plan=${encodeURIComponent(plan.id)}`)}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-4 rounded-xl font-black text-white shadow-lg transition-all hover:-translate-y-0.5 active:scale-[0.98]"
            style={{
              backgroundColor: FLARE_RED,
              boxShadow: isBest
                ? '0 14px 32px -12px rgba(196,21,28,0.6)'
                : '0 10px 24px -12px rgba(196,21,28,0.45)',
            }}
          >
            Choose This Plan
            <Icon path="M14 5l7 7m0 0l-7 7m7-7H3" style={ICON_STYLE_18} />
          </button>
          <div className="mt-3 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-widest">
            Secure checkout · Cancel anytime
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ShopPlansSection() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const r = await subscriptionPlanApi.getActive();
        if (cancelled) return;
        const body = r && r.data ? r.data : {};
        const rawList =
          body.subscription_plans ||
          body.plans ||
          body.data ||
          (Array.isArray(body) ? body : []) ||
          [];
        const list = (Array.isArray(rawList) ? rawList : [])
          .filter(p => p.status === undefined || String(p.status).toLowerCase() === 'active')
          .filter(p => p.includes_physical === undefined || Boolean(p.includes_physical) === true)
          .sort((a, b) => (Number(a.duration_months) || 0) - (Number(b.duration_months) || 0));
        setPlans(list);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load plans.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const maxSavePct = maxPlanSavingsPct(plans);
  const yearlyPlan = findPlanByMonths(plans, 12);
  const yearlySavePct = yearlyPlan ? calcSavingsPct(12, Number(yearlyPlan.price_pkr) || 0) : maxSavePct;

  return (
    <ShopLayout>
      <section
        className="relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg,#fff1f2 0%,#ffffff 40%,#ecfdf5 100%)' }}
      >
        <div
          className="absolute top-0 left-0 -ml-24 -mt-24 rounded-full opacity-25 pointer-events-none"
          style={{
            backgroundColor: FLARE_RED,
            filter: 'blur(80px)',
            width: '320px',
            height: '320px',
          }}
        />
        <div
          className="absolute bottom-0 right-0 -mr-24 -mb-24 rounded-full opacity-20 pointer-events-none"
          style={{
            backgroundColor: '#059669',
            filter: 'blur(90px)',
            width: '384px',
            height: '384px',
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 lg:pt-20 lg:pb-16">
          <div className="max-w-3xl mx-auto text-center">
            <span
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6 shadow-sm border"
              style={{
                backgroundColor: '#fff',
                color: FLARE_RED,
                borderColor: 'rgba(196,21,28,0.12)',
              }}
            >
              <Icon
                path="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                style={ICON_STYLE_14}
              />
              Printed Magazine Subscriptions
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight text-gray-900 mb-5">
              Subscribe &amp; Save Up To <span style={{ color: FLARE_RED }}>{maxSavePct || 40}% OFF</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed">
              Every issue of Flare Magazine delivered to your doorstep. The more months you subscribe, the more you save — plus free nationwide shipping on every plan.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gray-200 shadow-sm text-sm">
                <Icon path="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" style={ICON_STYLE_16} className="text-emerald-600 shrink-0" />
                <span className="font-bold text-gray-800">Secure Checkout</span>
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gray-200 shadow-sm text-sm">
                <Icon
                  path="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                  style={ICON_STYLE_16}
                  className="shrink-0"
                />
                <span className="font-bold text-gray-800" style={{ color: FLARE_RED }}>
                  Free Delivery All Pakistan
                </span>
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gray-200 shadow-sm text-sm">
                <Icon
                  path="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                  style={ICON_STYLE_16}
                  className="text-emerald-600 shrink-0"
                />
                <span className="font-bold text-gray-800">Cancel Anytime</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="rounded-3xl border border-gray-100 bg-white p-6 animate-pulse space-y-4 h-full">
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-10 bg-gray-200 rounded w-2/3"></div>
                <div className="h-5 bg-gray-100 rounded w-1/3"></div>
                <div className="space-y-2 pt-4">
                  {Array.from({ length: 5 }).map((__, j) => (
                    <div key={j} className="h-4 bg-gray-100 rounded"></div>
                  ))}
                </div>
                <div className="h-12 bg-gray-200 rounded-xl mt-6"></div>
              </div>
            ))}
          </div>
        )}

        {error && !loading && (
          <div
            className="p-6 rounded-2xl border text-sm font-semibold"
            style={{ backgroundColor: '#fff1f2', borderColor: '#fecdd3', color: '#9f1239' }}
          >
            Couldn&apos;t load subscription plans: {error}. Please refresh or try again later.
          </div>
        )}

        {!loading && !error && plans.length === 0 && (
          <div className="p-12 rounded-3xl bg-white border border-dashed border-gray-300 text-center">
            <div
              className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center"
              style={{ backgroundColor: '#f3f4f6' }}
            >
              <Icon
                path="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                style={ICON_STYLE_32}
                className="text-gray-400"
              />
            </div>
            <h3 className="text-xl font-black text-gray-900 mb-2">Subscription plans coming soon</h3>
            <p className="text-gray-600 mb-6">We&apos;re finalizing our printed magazine subscription tiers. Check back shortly!</p>
            <Link
              to="/magazines"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-white shadow-md"
              style={{ backgroundColor: FLARE_RED }}
            >
              Browse Individual Issues
              <Icon path="M14 5l7 7m0 0l-7 7m7-7H3" style={ICON_STYLE_18} />
            </Link>
          </div>
        )}

        {!loading && plans.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-14">
              {plans.map((plan) => (
                <PlanCard key={plan.id} plan={plan} />
              ))}
            </div>

            <div
              className="relative rounded-3xl overflow-hidden border p-8 lg:p-10"
              style={{
                backgroundImage: 'linear-gradient(to bottom right,#f9fafb,#ffffff,#fff1f2)',
                borderColor: '#f3f4f6',
              }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-2xl lg:text-3xl font-black text-gray-900 mb-3 leading-tight">
                    Not ready to subscribe yet?
                  </h2>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Prefer to try one issue first? No problem. Browse our latest magazine issues and buy singles — digital or printed.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Link
                      to="/magazines"
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-xl font-bold border-2 transition-colors"
                      style={{ borderColor: FLARE_RED, color: FLARE_RED, backgroundColor: '#fff' }}
                    >
                      Shop Latest Issues
                      <Icon path="M14 5l7 7m0 0l-7 7m7-7H3" style={ICON_STYLE_18} />
                    </Link>
                    <a
                      href="#contact"
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-gray-800 bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <Icon
                        path="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        style={ICON_STYLE_18}
                      />
                      Contact Sales
                    </a>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="rounded-2xl bg-white border border-gray-100 p-5 text-center shadow-sm">
                    <div className="text-3xl font-black" style={{ color: FLARE_RED }}>
                      {MONTH_BASELINE_PRINT.toLocaleString('en-PK')}
                    </div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-widest mt-1">
                      Cover Price
                    </div>
                  </div>
                  <div className="rounded-2xl bg-white border border-gray-100 p-5 text-center shadow-sm">
                    <div className="text-3xl font-black text-emerald-600">{yearlySavePct || 33}%</div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-widest mt-1">
                      Yearly Save
                    </div>
                  </div>
                  <div className="rounded-2xl bg-white border border-gray-100 p-5 text-center shadow-sm">
                    <div className="text-3xl font-black text-gray-800">FREE</div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-widest mt-1">
                      PK Delivery
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </section>
    </ShopLayout>
  );
}
